/**
 * HTTP client wrapper around fetch.
 *
 * - Sends cookies (`credentials: 'include'`) so the httpOnly admin auth cookie
 *   set by the NestJS server is round-tripped.
 * - JSON in, JSON out by default. Multipart upload helpers will come in Task 25.
 * - Throws `ApiError` with a typed message field carrying the server's i18n key
 *   when available (server contract: `{ message: string; code?: string }`).
 */

export interface ApiErrorBody {
  message: string;
  code?: string;
  details?: unknown;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly details: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

const DEFAULT_HEADERS: HeadersInit = {
  'Content-Type': 'application/json',
  // Custom header used as a lightweight CSRF mitigation in tandem with
  // SameSite=Strict cookies; the server validates its presence on state-changing
  // requests.
  'X-Requested-With': 'displaycms-admin',
};

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function request<TResponse>(
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<TResponse> {
  const url = path.startsWith('/') ? `/api${path}` : `/api/${path}`;

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      ...DEFAULT_HEADERS,
      ...(options.headers ?? {}),
    },
  };
  if (options.signal) {
    init.signal = options.signal;
  }
  if (body !== undefined && body !== null) {
    init.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    throw new ApiError(0, {
      message: 'errors.network',
      code: 'network_error',
      details: cause instanceof Error ? cause.message : String(cause),
    });
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    let errorBody: ApiErrorBody = { message: 'errors.generic' };
    if (isJson) {
      try {
        const parsed = (await response.json()) as Partial<ApiErrorBody>;
        errorBody = {
          message:
            typeof parsed.message === 'string' && parsed.message.length > 0
              ? parsed.message
              : 'errors.generic',
          ...(typeof parsed.code === 'string' ? { code: parsed.code } : {}),
          ...(parsed.details !== undefined ? { details: parsed.details } : {}),
        };
      } catch {
        // keep generic
      }
    }
    throw new ApiError(response.status, errorBody);
  }

  if (!isJson) {
    return undefined as TResponse;
  }

  return (await response.json()) as TResponse;
}

export function apiGet<TResponse>(
  path: string,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>('GET', path, undefined, options);
}

export function apiPost<TResponse>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>('POST', path, body ?? null, options);
}

export function apiPatch<TResponse>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>('PATCH', path, body ?? null, options);
}

export function apiDelete<TResponse = void>(
  path: string,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>('DELETE', path, undefined, options);
}

export function apiPut<TResponse>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<TResponse> {
  return request<TResponse>('PUT', path, body ?? null, options);
}
